import { v4 as uuidv4 } from 'uuid';

function mapNotes(notes) {
  return notes.map((n) => n.description).join(', ');
}

class Walker {
  constructor(config, data, notes) {
    this.config = config;
    this.data = data;
    this.notes = notes;
    this.total = new Total(this);
    this.sections = null;
    this.total_records = null;
  }

  build() {
    let section;
    let idx = 0;

    this.sections = [];
    this.total_records = this.data.length;

    while (idx < this.total_records) {
      section = new Section(this, idx);
      section.build();
      idx = section.stop_idx;
      this.total.add(section.total);
      this.sections.push(section);
    }
  }

  getNotes(row, noteDetailsColumn) {
    let key = (row.main_id === null ? '' : row.main_id) + '-';

    key += (row.sub_id === null ? '' : row.sub_id) + '-';
    key += (row.third_id === null ? '' : row.third_id) + '-';
    key += (row.fourth_id === null ? '' : row.fourth_id) + '-';

    if (noteDetailsColumn && noteDetailsColumn in row) {
      key = key + (row[noteDetailsColumn] || '');
    }

    return this.notes[key] || [];
  }
}

class Total {
  constructor(walker) {
    Object.defineProperties(this, {
      walker: {
        enumerable: false,
        configurable: false,
        writable: false,
        value: walker,
      },
      aggregates: {
        enumerable: false,
        configurable: false,
        writable: false,
        value: (walker.config.fields && walker.config.fields.aggregates) || [],
      },
      unique_fields: {
        enumerable: false,
        configurable: false,
        writable: false,
        value: (walker.config.fields && walker.config.fields.unique) || [],
      },
    });
    this.num_rows = 0;
  }

  add(record) {
    this.aggregates.forEach((f) => {
      if (record[f] !== undefined && record[f] !== null) {
        this[f] = (this[f] || 0) + parseFloat(record[f]);
      }
    });
    this.unique_fields.forEach((f) => {
      const d = record[f];

      if (this[f] === undefined) {
        this[f] = [d];
      } else {
        if (this[f].indexOf(d) === -1) {
          this[f].push(d);
        }
      }
    });
    this.num_rows += 1;
  }
}

class Section {
  constructor(walker, idx) {
    Object.defineProperties(this, {
      walker: {
        enumerable: false,
        configurable: false,
        writable: false,
        value: walker,
      },
      start_idx: {
        enumerable: false,
        configurable: false,
        writable: false,
        value: idx,
      },
      stop_idx: {
        enumerable: false,
        configurable: false,
        writable: true,
        value: null,
      },
    });
    this.uuid = uuidv4();
    this.total = new Total(walker);
    this.main = walker.data[idx].main;
    this.main_id = walker.data[idx].main_id;

    // Add sub info for grouping
    if (walker.config.group_by_sub_id) {
      this.sub = walker.data[idx].sub;
      this.sub_id = walker.data[idx].sub_id;
    }

    this.rows = [];
  }

  build() {
    const data = this.walker.data;
    const totalRecords = data.length;
    const noteDetailsColumn = (this.walker.config.fields && this.walker.config.fields.note_detail_column) || null;
    const rollups = this.walker.config.rollups;
    const reset = (this.walker.config.fields && this.walker.config.fields.reset) || {};
    const groupBySubId = this.walker.config.group_by_sub_id;
    let prevSubId;
    let prevThirdId;
    let prevFourthId;
    let firstRecord = true;
    let idx = this.start_idx;
    let stripe = 0;
    let row;

    const isSameSection = (record) => {
      return record.main_id === this.main_id && // Main matches
        (!groupBySubId || record.sub_id === this.sub_id); // Sub matches
    };

    while (idx < totalRecords && isSameSection(data[idx])) {
      const record = data[idx];
      const notes = this.walker.getNotes(record, noteDetailsColumn);
      const notesSummary = mapNotes(notes);
      let skipYield = false;

      row = {
        record,
        uuid: uuidv4(),
        sub_show_data: firstRecord || prevSubId !== record.sub_id,
        notes,
        notes_summary: notesSummary,
        stripe,
      };

      row.third_show_data = row.sub_show_data || prevThirdId !== record.third_id;
      row.fourth_show_data = row.third_show_data || prevFourthId !== record.fourth_id;

      firstRecord = false;

      if (row.fourth_show_data) {
        prevSubId = record.sub_id;
        prevThirdId = record.third_id;
        prevFourthId = record.fourth_id;
        stripe += 1;
        row.stripe = stripe;

        if (rollups) {
          let rollupIdx = idx;
          const rollup = new Total(this.walker);
          const mainId = record.main_id;
          const subId = record.sub_id;
          const thirdId = record.third_id;
          const fourthId = record.fourth_id;
          let seenBlank = false;
          let seenNonBlank = false;
          // Iterate through until we get to the end or another rollup group

          while (
            rollupIdx < totalRecords &&
            data[rollupIdx].main_id === mainId &&
            data[rollupIdx].sub_id === subId &&
            data[rollupIdx].third_id === thirdId &&
            data[rollupIdx].fourth_id === fourthId
            ) {
            const rollupRecord = data[rollupIdx];

            rollup.add(rollupRecord);
            if (rollupRecord[noteDetailsColumn]) {
              seenNonBlank = true;
            } else {
              seenBlank = true;
            }
            rollupIdx += 1;
          }

          let addAggregate = false;

          if (rollups === 'blank') {
            if (!seenNonBlank) {
              addAggregate = true;
              skipYield = true;
            } else if (!seenBlank) {
              // add an aggregate row even though we haven't seen a blank
              addAggregate = true;
              row.notes = this.walker.getNotes(record);
              row.notes_summary = mapNotes(row.notes);
            } else {
              addAggregate = true;
              row.notes = '';
              row.notes_summary = '';
            }
          } else {
            if (rollup.num_rows > 1) {
              addAggregate = true;
              row.notes = '';
              row.notes_summary = '';
            }
          }

          if (addAggregate) {
            row.aggregate = rollup;
            row.record = Object.assign({}, record);
            for (const f in reset) {
              row.record[f] = reset[f];
            }
            this.rows.push(row);
            row = Object.assign({}, row);
            row.record = record;
            row.aggregate = null;
            row.sub_show_data = false;
            row.third_show_data = false;
            row.fourth_show_data = false;
          }
        } else {
          if (noteDetailsColumn in row) {
            const projNotes = this.walker.getNotes(record);

            if (projNotes.length > 0) {
              row.record = Object.assign({}, record);
              for (const f in reset) {
                row.record[f] = reset[f];
              }
              row.notes = projNotes;
              row.notes_summary = mapNotes(projNotes);
              this.rows.push(row);
              row = Object.assign({}, row);
              row.record = record;
              row.notes = '';
              row.notes_summary = '';
            }
          }
        }
      }

      if (!skipYield) {
        row.notes = notes;
        row.notes_summary = notesSummary;
        this.rows.push(row);
      }

      this.total.add(record);
      idx += 1;
    }

    this.stop_idx = idx;
  }
}

export default (reportData, notes, walkerConfig) => {
  const walker = new Walker(walkerConfig, reportData || [], notes || {});

  walker.build();

  return { sections: walker.sections, total: walker.total, total_records: walker.total_records };
};
